import useAppUser from 'hooks/useAppUser';
import { createColumnHelper } from '@tanstack/react-table';
import { automatedReviewLogsQuery } from 'components/domain/review/automatedReviewLogs/automatedReviewLogsQuery';
import useFilterFormContext from 'components/filters/useFilterFormContext';
import useServerFilteredTable from 'components/table/hooks/useServerFilteredTable';
import { AutomatedReviewLog } from 'models/automation/Reviews';
import { useTranslation } from 'react-i18next';
import { AutomatedReviewLogsFiltersSchemaType } from '../filters/AutomatedReviewLogsFilters.schema';
import useSearchedAutomatedReviewLogsLeadUid from '../filters/useSearchedAutomatedReviewLogsLeadUid';
import ReviewDateCell from './cells/ReviewDateCell';
import GuestNameCell from './cells/GuestNameCell';
import ChannelCell from './cells/ChannelCell';
import StatusCell from './cells/StatusCell';
import ActionsCell from './cells/ActionsCell';
import { AutomatedReviewLogsTableStyled } from './AutomatedReviewLogsTable.styles';

const AutomatedReviewLogsTable = () => {
  const { agencyUid } = useAppUser();
  const { t } = useTranslation();
  const { lastSubmittedFilters } =
    useFilterFormContext<AutomatedReviewLogsFiltersSchemaType>();
  const searchedLeadUid = useSearchedAutomatedReviewLogsLeadUid();
  const columnHelper = createColumnHelper<AutomatedReviewLog>();

  const columns = [
    columnHelper.accessor('reviewDate', {
      header: t('pageAutomations.reviews.automatedReviewLogs.table.reviewDate'),
      cell: ReviewDateCell,
      meta: {
        dateFormat: 'PP',
        showCellTooltip: false,
      },
    }),
    columnHelper.accessor('reviewTemplateTitle', {
      header: t(
        'pageAutomations.reviews.automatedReviewLogs.table.reviewTemplateTitle',
      ),
    }),
    columnHelper.accessor('guest.name', {
      header: t('pageAutomations.reviews.automatedReviewLogs.table.guestName'),
      cell: GuestNameCell,
    }),
    columnHelper.accessor('channel', {
      header: t('pageAutomations.reviews.automatedReviewLogs.table.channel'),
      cell: ChannelCell,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.accessor('property.name', {
      header: t('pageAutomations.reviews.automatedReviewLogs.table.property'),
    }),
    columnHelper.accessor('status', {
      header: t('pageAutomations.reviews.automatedReviewLogs.table.status'),
      cell: StatusCell,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: ActionsCell,
      meta: {
        showCellTooltip: false,
      },
    }),
  ];

  const { isLoading, tableInstance } = useServerFilteredTable({
    columns,
    getQuery: (options) =>
      automatedReviewLogsQuery({
        filters: lastSubmittedFilters ?? {},
        leadUid: searchedLeadUid,
        ...options,
        agencyUid: agencyUid!,
      }),
    pageSize: 20,
  });

  return (
    <AutomatedReviewLogsTableStyled
      tableInstance={tableInstance}
      rightOffset={100}
      isLoading={isLoading}
      data-testid="automated-review-logs-table"
      emptyState={t(
        'pageAutomations.reviews.automatedReviewLogs.table.emptyTable',
      )}
    />
  );
};

export default AutomatedReviewLogsTable;
